import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  LogOut, ExternalLink, Loader2,
  LayoutDashboard, Inbox, Users, Settings as SettingsIcon, CreditCard,
} from "lucide-react";
import DashboardView from "./admin/DashboardView";
import InquiriesView from "./admin/InquiriesView";
import ClientsView from "./admin/ClientsView";
import SettingsView from "./admin/SettingsView";
import PaymentsView from "./admin/PaymentsView";

const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "inquiries", label: "Demandes", Icon: Inbox },
  { key: "clients", label: "Clients", Icon: Users },
  { key: "payments", label: "Payments", Icon: CreditCard },
  { key: "settings", label: "Paramètres", Icon: SettingsIcon },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [inquiries, setInquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openClientId, setOpenClientId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");
  const email = localStorage.getItem("admin_email");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, c, s, set] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/customers"),
        api.get("/admin/staff"),
        api.get("/admin/settings"),
      ]);
      setInquiries(i.data);
      setCustomers(c.data);
      setStaff(s.data);
      setSettings(set.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  useEffect(() => {
    const handler = (e) => {
      setTab("clients");
      setOpenClientId(e.detail);
    };
    window.addEventListener("safemat:openCustomer", handler);
    return () => window.removeEventListener("safemat:openCustomer", handler);
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  const openCustomer = (id) => {
    setOpenClientId(id);
    setTab("clients");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <header className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/safemat-logo.png" alt="SAFEMAT" className="h-10 w-auto -my-2" />
            <span className="font-display font-black tracking-tight text-sm text-slate-400">/ ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
              <ExternalLink size={11} /> Site
            </Link>
            <span className="hidden sm:inline text-xs text-slate-500">{email}</span>
            <button
              onClick={logout}
              className="btn-secondary !text-xs !py-1.5 !px-3 !text-white !border-slate-700"
              data-testid="admin-logout-btn"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex gap-1 border-t border-slate-800 overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`py-3 px-4 text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap flex items-center gap-2 ${
                tab === key ? "text-white border-b-2 border-[#016C36]" : "text-slate-400 hover:text-white"
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {loading ? (
          <p className="text-sm text-slate-500">
            <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
          </p>
        ) : (
          <>
            {tab === "dashboard" && (
              <DashboardView
                staff={staff}
                switchTab={setTab}
                openCustomer={openCustomer}
                reloadGlobal={load}
              />
            )}
            {tab === "inquiries" && (
              <InquiriesView items={inquiries} reload={load} />
            )}
            {tab === "clients" && (
              <ClientsView
                items={customers}
                settings={settings}
                staff={staff}
                reload={load}
                openId={openClientId}
                clearOpenId={() => setOpenClientId(null)}
              />
            )}
            {tab === "payments" && (
              <PaymentsView customers={customers} reloadGlobal={load} />
            )}
            {tab === "settings" && <SettingsView />}
          </>
        )}
      </main>
    </div>
  );
}
